"use client";

import { useTranslations } from "@frontend/lib/next-intl-typed";
import { useEffect, useRef, useState } from "react";
import {
  KioskButton,
  KioskDialog,
  KioskDialogContent,
  KioskDialogDescription,
  KioskDialogFooter,
  KioskDialogHeader,
  KioskDialogTitle,
} from "../../elements";
import { useKiosk } from "../kiosk-provider";

interface InactivityTimeoutProps {
  /**
   * Timeout duration in milliseconds before showing the dialog.
   * @default 300000 (5 minutes)
   */
  timeoutDuration?: number;
  /**
   * Callback when user confirms they are still present
   */
  onStillHere?: () => void;
  /**
   * Callback when user times out (can be used to restart/reset the flow)
   */
  onTimeout?: () => void;
  /**
   * Whether to show the "Are you still here?" dialog before timeout.
   * If false, will directly call onTimeout without showing dialog.
   * @default true
   */
  showDialog?: boolean;

  doNotTimeOut?: boolean;
}

/**
 * Inactivity Timeout Component
 *
 * Monitors user activity (touch on mobile/tablets, mouse movement on desktop)
 * and shows a "Are you still here?" dialog after a period of inactivity.
 *
 * @example
 * ```tsx
 * <InactivityTimeout
 *   timeoutDuration={300000} // 5 minutes
 *   onStillHere={() => console.log("User still here")}
 *   onTimeout={() => console.log("User timed out")}
 * />
 * ```
 */
export function InactivityTimeout({
  timeoutDuration = 300000, // 5 minutes default
  onStillHere,
  onTimeout,
  showDialog = true,
  doNotTimeOut = false,
}: InactivityTimeoutProps) {
  const { state } = useKiosk();
  const qrMode = state.context.qrMode === true;
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dialogTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const t = useTranslations("Kiosk.MedriteCheckin.InactivityTimeout");

  const resetTimeout = () => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Close dialog if it's open
    if (isOpen) {
      console.log("[InactivityTimeout] resetTimeout — closing open dialog", {
        showDialog,
        doNotTimeOut,
      });
      setIsOpen(false);
    }

    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      console.log("[InactivityTimeout] timer fired", {
        showDialog,
        doNotTimeOut,
        isOpen,
      });
      if (showDialog) {
        // Show dialog and wait for user response
        console.log("[InactivityTimeout] opening dialog");
        setIsOpen(true);

        // Auto-timeout after 30 seconds if no response
        dialogTimeoutRef.current = setTimeout(() => {
          console.log(
            "[InactivityTimeout] 30s auto-timeout — closing dialog, calling onTimeout",
          );
          setIsOpen(false);
          onTimeout?.();
        }, 30000);
      } else {
        // Skip dialog, directly call timeout handler
        console.log(
          "[InactivityTimeout] showDialog=false, calling onTimeout silently",
        );
        onTimeout?.();
      }
    }, timeoutDuration);
  };

  const handleStillHere = () => {
    console.log("[InactivityTimeout] user clicked 'I'm here'");
    // Clear dialog auto-timeout
    if (dialogTimeoutRef.current) {
      clearTimeout(dialogTimeoutRef.current);
    }

    setIsOpen(false);
    onStillHere?.();
    resetTimeout();
  };

  // Reset isOpen when entering a screen where the dialog shouldn't appear
  // (showDialog off or doNotTimeOut on). Prevents the dialog rendering on
  // top of loading spinners or persisting through a transition to welcome/standby.
  useEffect(() => {
    if ((!showDialog || doNotTimeOut) && isOpen) {
      setIsOpen(false);
    }
  }, [showDialog, doNotTimeOut, isOpen]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (qrMode) {
      console.log(
        "qrMode, so not going to ask the user if they are still here",
      );
      return;
    }

    // Activity event handlers — only reset the timer if we're not on a
    // doNotTimeOut screen (standby, loading), otherwise user interaction
    // during loading/standby would arm a new timer.
    const handleActivity = () => {
      if (!doNotTimeOut) {
        resetTimeout();
      }
    };

    // Touch events for mobile/tablet
    const events = [
      "touchstart",
      "touchmove",
      "mousemove",
      "mousedown",
      "keydown",
    ];

    // Add event listeners
    for (const event of events) {
      window.addEventListener(event, handleActivity, { passive: true });
    }

    // Start initial timeout
    if (!doNotTimeOut) {
      console.log("[InactivityTimeout] effect setup — starting timer", {
        showDialog,
        doNotTimeOut,
        timeoutDuration,
      });
      resetTimeout();
    } else {
      console.log(
        "[InactivityTimeout] effect setup — doNotTimeOut, skipping timer",
        { showDialog, doNotTimeOut },
      );
    }

    // Cleanup
    return () => {
      console.log("[InactivityTimeout] effect cleanup — clearing timers");
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (dialogTimeoutRef.current) {
        clearTimeout(dialogTimeoutRef.current);
      }
      for (const event of events) {
        window.removeEventListener(event, handleActivity);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeoutDuration, showDialog, doNotTimeOut, qrMode]);

  // Guard: never render the dialog on screens where it shouldn't appear.
  // Even if a stale timer sets isOpen=true, the dialog JSX won't be in the
  // tree on welcome/standby/qrMode screens.
  if (isOpen && (!showDialog || qrMode)) {
    console.warn("[InactivityTimeout] render guard caught stale isOpen=true", {
      showDialog,
      qrMode,
    });
  }
  if (qrMode || !showDialog) return null;

  return (
    <KioskDialog open={isOpen} onOpenChange={setIsOpen}>
      <KioskDialogContent className="sm:max-w-md w-[90vw]">
        <KioskDialogHeader>
          <KioskDialogTitle className="flex items-center gap-3 justify-center !text-[24px]">
            {t("AreYouStillHere")}
          </KioskDialogTitle>
          <KioskDialogDescription>
            {t("WeHaventDetectedAnyActivityForAWhile")}
          </KioskDialogDescription>
        </KioskDialogHeader>
        <KioskDialogFooter>
          <KioskButton
            type="button"
            variant="primary"
            onClick={handleStillHere}
            className="w-full"
          >
            {t("ImHere")}
          </KioskButton>
        </KioskDialogFooter>
      </KioskDialogContent>
    </KioskDialog>
  );
}
